import { useQuery } from '@tanstack/react-query'
import { useCampaignClient } from '@/lib/api/ts-rest-client'
import { CACHE_KEYS } from '@/lib/cache-utils'
import { useEffect, useMemo, useState, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTsrClient } from './tsr'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@alphabait/ui' // select'
import { SearchInput } from '@alphabait/ui' // input'
import { useTranslation } from 'react-i18next'

export function EndUsersPage() {
  const [searchParams, setSearchParams] = useSearchParams()

  const [params, setParams] = useState({
    searchTerm: searchParams.get('searchTerm') || undefined,
    selectedCustomerId: searchParams.get('selectedCustomerId') || undefined,
    currentPage: parseInt(searchParams.get('page') || '1', 10),
    itemsPerPage: parseInt(searchParams.get('itemsPerPage') || '10'),
    sortBy: searchParams.get('sortBy') || 'createdAt',
    sortOrder: (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc',
  })

  const tsr = useTsrClient()
  const campaignClient = useCampaignClient()

  const { data: customersData, isLoading: customersLoading } = useQuery({
    queryKey: [CACHE_KEYS.CUSTOMERS],
    queryFn: async () => {
      const response = await campaignClient.customers.query()
      return response.status === 200 ? response.body : []
    },
  })

  const customers = useMemo(
    () => customersData || [],
    [customersData],
  )

  const {
    data: endUsersRpcResponse,
    isPending: endUsersPending,
    error: endUsersTanStackError, // This is the error object from TanStack Query
  } = tsr.endUser.get.useQuery({
    queryKey: [
      'endUsers',
      params.selectedCustomerId,
      params.searchTerm,
      params.currentPage,
      params.itemsPerPage,
      params.sortBy,
      params.sortOrder,
    ],
    queryData: {
      query: {
        customerId: params.selectedCustomerId || undefined,
        search: params.searchTerm || undefined,
        pagination: {
          page: params.currentPage,
          perPage: params.itemsPerPage,
        },
        sort: {
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
        },
      },
    },
  })

  useEffect(() => {
    const urlParams = new URLSearchParams()

    if (params.searchTerm) {
      urlParams.set('searchTerm', params.searchTerm)
    }
    if (params.selectedCustomerId) {
      urlParams.set('selectedCustomerId', params.selectedCustomerId)
    }

    // Only set params if they differ from their default values
    if (params.currentPage !== 1) {
      urlParams.set('page', params.currentPage.toString())
    }
    if (params.itemsPerPage !== 10) {
      urlParams.set('itemsPerPage', params.itemsPerPage.toString())
    }
    if (params.sortBy !== 'createdAt') {
      urlParams.set('sortBy', params.sortBy)
    }
    if (params.sortOrder !== 'desc') {
      urlParams.set('sortOrder', params.sortOrder)
    }

    setSearchParams(urlParams, { replace: true })
  }, [
    params.searchTerm,
    params.selectedCustomerId,
    params.currentPage,
    params.itemsPerPage,
    params.sortBy,
    params.sortOrder,
    setSearchParams,
  ])
  const { t } = useTranslation()

  const rowContentClassName = 'px-5 py-4 text-sm text-gray-100'
  const tableHeadersClassName =
    'whitespace-nowrap px-5 py-4 text-left text-sm font-semibold text-gray-300 uppercase cursor-pointer'

  const handleSort = (columnKey: string) => {
    if (params.sortBy === columnKey) {
      setParams((prev) => ({
        ...prev,
        sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc',
        currentPage: 1,
      }))
    } else {
      setParams((prev) => ({
        ...prev,
        sortBy: columnKey,
        sortOrder: 'asc',
      }))
    }
  }

  const SortIndicator = ({ columnKey }: { columnKey: string }) => {
    if (params.sortBy === columnKey) {
      return params.sortOrder === 'asc' ? <> ▲</> : <> ▼</>
    }
    return null
  }

  const overallIsLoading = endUsersPending || customersLoading

  const { users, errorMessage } = useMemo(() => {
    if (endUsersTanStackError) {
      // TanStack Query error object might have a `message` property directly
      // or it might be nested depending on how ts-rest wraps it.
      // For a generic approach, try to access common error message paths.
      const message =
        (endUsersTanStackError as any)?.message || 'An unknown error occurred.'
      return { users: [], errorMessage: message }
    }

    if (endUsersRpcResponse) {
      if (endUsersRpcResponse.status === 200) {
        const responseBody = Array.isArray(endUsersRpcResponse.body)
          ? endUsersRpcResponse.body
          : []
        return { users: responseBody, errorMessage: null }
      } else {
        // Handle non-200 status from ts-rest (which are considered errors by ts-rest client)

        const errorBody = endUsersRpcResponse.body as any // Cast to any to access potential message
        if (errorBody && typeof errorBody.message === 'string') {
          return { users: [], errorMessage: errorBody.message }
        }
        return {
          users: [],
          errorMessage: `Request failed with status ${endUsersRpcResponse.status}`,
        }
      }
    }
    return { users: [], errorMessage: null }
  }, [endUsersRpcResponse, endUsersTanStackError])

  const handleSearchTermChange = useCallback((value: string) => {
    value &&
      setParams((prev) => ({
        ...prev,
        searchTerm: value,
        currentPage: 1,
      }))
  }, [])

  if (overallIsLoading && !errorMessage && users.length === 0)
    return <div className="p-8">{t('loading')}</div>
  if (errorMessage && !overallIsLoading)
    return (
      <div className="p-8 text-red-500">
        {t('error_prefix')}
        {errorMessage === 'An unknown error occurred.'
          ? t('unknown_error_occurred')
          : errorMessage.includes('Request failed with status')
            ? t('end_users_page.request_failed_status', {
              status: errorMessage.split(' ').pop(),
            })
            : errorMessage}
      </div>
    )

  return (
    <div className="bg-ui-100 p-8 h-full">
      <div className="p-8 sm:rounded-lg bg-white overflow-x-auto">
        <h1 className="font-bold text-4xl pb-10 leading-none mb-0">
          {t('end_users_page.title')}
        </h1>

        <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="customer-filter"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t('end_user_list.filter_by_customer')}
            </label>
            <Select
              value={params.selectedCustomerId || ''}
              onValueChange={(value: string) => {
                setParams((prev) => ({
                  ...prev,
                  selectedCustomerId: value || undefined,
                  currentPage: 1,
                }))
              }}
              disabled={customersLoading || endUsersPending}
            >
              <SelectTrigger id="customer-filter" className="w-full">
                <SelectValue placeholder={t('end_user_list.all_customers')} />
              </SelectTrigger>
              <SelectContent>
                {customers.map((customer: { id: string; name: string }) => (
                  <SelectItem key={customer.id} value={customer.id}>
                    {customer.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label
              htmlFor="search-filter"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t('end_user_list.search_label')}
            </label>
            <SearchInput
              id="search-filter"
              onSearch={handleSearchTermChange}
              defaultValue={params.searchTerm}
              disabled={endUsersPending}
              placeholder={t('end_user_list.search_placeholder')}
              labelText={t('end_user_list.search_placeholder')} // Assuming same as placeholder, adjust if different label text needed
            />
          </div>
        </div>

        <div className="border sm:rounded-lg overflow-auto">
          <table className="w-full table-auto min-w-max">
            <thead className="bg-ui">
              <tr>
                <th scope="col" className={tableHeadersClassName}>
                  {t('end_user_list.table_header_customer')}
                </th>
                <th
                  scope="col"
                  className={tableHeadersClassName}
                  onClick={() => handleSort('first_name')}
                >
                  {t('end_user_list.table_header_name')}
                  <SortIndicator columnKey={'first_name'} />
                </th>
                <th
                  scope="col"
                  className={tableHeadersClassName}
                  onClick={() => handleSort('email')}
                >
                  {t('end_user_list.table_header_email')}
                  <SortIndicator columnKey="email" />
                </th>
                <th scope="col" className={tableHeadersClassName}>
                  {t('end_user_list.table_header_address')}
                </th>
                <th scope="col" className={tableHeadersClassName}>
                  {t('end_user_list.table_header_campaigns')}
                </th>
                <th scope="col" className={tableHeadersClassName}>
                  {t('end_user_list.table_header_actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {users.map((user) => (
                <tr key={`user-${user.id}`}>
                  <td className={rowContentClassName}>{user.customer.name}</td>
                  <td className={rowContentClassName}>
                    {[user.firstName, user.middle, user.lastName]
                      .filter(Boolean)
                      .join(' ')}
                  </td>
                  <td className={rowContentClassName}>{user.email || 'N/A'}</td>
                  <td className={rowContentClassName}>
                    {user.address && typeof user.address === 'object' // Added type check for address
                      ? [
                        user.address.country,
                        user.address.city,
                        user.address.postcode,
                        user.address.streetName,
                        user.address.houseNumber,
                      ]
                        .filter(Boolean)
                        .join(' ')
                      : 'N/A'}
                  </td>
                  <td className={rowContentClassName}>
                    <ul>
                      {user.endUserCampaigns.map((campaignItem) => (
                        <li
                          key={campaignItem?.campaign?.id || campaignItem?.id}
                        >
                          {campaignItem?.campaign?.name || 'N/A'}
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className={rowContentClassName}>
                    <Link
                      to={`/end-users/${user.id}`}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      {t('end_user_list.view_profile_action')}
                    </Link>
                  </td>
                </tr>
              ))}
              {users.length === 0 && !overallIsLoading && !errorMessage && (
                <tr>
                  <td colSpan={6} className="text-center py-4">
                    {' '}
                    {/* Adjusted colSpan to 6 to match new number of columns */}
                    {t('end_user_list.no_users_found')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {(users.length > 0 || params.currentPage > 1) && !errorMessage && (
          <div className="mt-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-700">
                {t('end_user_list.pagination_page_info', {
                  currentPage: params.currentPage,
                })}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() =>
                  setParams((prev) => ({
                    ...prev,
                    currentPage: Math.max(prev.currentPage - 1, 1),
                  }))
                }
                disabled={params.currentPage === 1 || overallIsLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('end_user_list.pagination_previous')}
              </button>
              <button
                onClick={() =>
                  setParams((prev) => ({
                    ...prev,
                    currentPage: Math.max(prev.currentPage + 1, 1),
                  }))
                }
                disabled={
                  users.length < params.itemsPerPage || overallIsLoading
                }
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('end_user_list.pagination_next')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
